import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Checkbox,
  OutlinedInput,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesAPI } from '../../shared/services/api';

import toast from 'react-hot-toast';

const CreateEditRole = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    userType: '',
    portalAccess: [],

    isActive: true
  });



  // Fetch role data for editing
  const { data: roleData, isLoading: roleLoading, error: roleError } = useQuery({
    queryKey: ['role', id],
    queryFn: () => rolesAPI.getRole(id),
    enabled: isEdit,
  });

  // Create role mutation
  const createMutation = useMutation({
    mutationFn: rolesAPI.createRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      toast.success('Role created successfully');
      navigate('/roles');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create role');
    },
  });

  // Update role mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => rolesAPI.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      queryClient.invalidateQueries(['role', id]);
      toast.success('Role updated successfully');
      navigate('/roles');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update role');
    },
  });

  // Load role data when editing
  useEffect(() => {
    console.log("roleData", roleData?.data?.data);
    if (isEdit && roleData?.data) {
      const role = roleData.data.data;
      setFormData({
        name: role.name || '',
        description: role.description || '',
        userType: role.userType || '',
        portalAccess: role.portalAccess || [],
        isActive: role.isActive !== false
      });
    }
  }, [isEdit, roleData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isEdit) {
      updateMutation.mutate({ id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePortalAccessChange = (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      portalAccess: typeof value === 'string' ? value.split(',') : value
    });
  };

  // Check if the current role is admin type (superadmin or admin)
  const isAdminRole = () => {
    return formData.userType === 'admin';
  };

  const userTypes = ['member', 'corporate', 'provider', 'intermediary', 'insurer', 'irda', 'admin'];
  const portals = ['member', 'corporate', 'provider', 'intermediary', 'insurer', 'irda', 'admin'];

  if (roleLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (roleError) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/roles')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Edit Role</Typography>
        </Box>
        <Alert severity="error">
          Failed to load role data: {roleError.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/roles')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">
            {isEdit ? 'Edit Role' : 'Create New Role'}
          </Typography>
          {isEdit && roleData?.data?.data && (
            <Typography variant="body2" color="text.secondary">
              Editing: {roleData.data.data.name}
            </Typography>
          )}
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} columns={12}>
          {/* Basic Information */}
          <Grid size={{xs: 12, sm:12, md: 12}}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{xs: 12, sm: 6}}>
                  <TextField
                    fullWidth
                    label="Role Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={createMutation.isPending || updateMutation.isPending}
                  />
                </Grid>
                
                <Grid size={{xs: 12, sm: 6}}>
                  <FormControl fullWidth required>
                    <InputLabel>User Type</InputLabel>
                    <Select
                      value={formData.userType}
                      label="User Type"
                      onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      {userTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={3}
                    disabled={createMutation.isPending || updateMutation.isPending}
                  />
                </Grid>
                {/* Portal Access */}
                  <Grid size={{xs: 12, sm: 6}}>
                      <FormControl fullWidth>
                        <InputLabel>Portal Access</InputLabel>
                        {isAdminRole() ? (
                          <Select
                            multiple
                            value={formData.portalAccess}
                            onChange={handlePortalAccessChange}
                            input={<OutlinedInput label="Portal Access" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip
                                    key={value}
                                    label={value.charAt(0).toUpperCase() + value.slice(1)}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            )}
                            disabled={createMutation.isPending || updateMutation.isPending}
                          >
                            {portals.map((portal) => (
                              <MenuItem key={portal} value={portal}>
                                <Checkbox checked={formData.portalAccess.indexOf(portal) > -1} />
                                <ListItemText primary={portal.charAt(0).toUpperCase() + portal.slice(1)} />
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Select
                            value={formData.portalAccess[0] || ''}
                            onChange={(e) => setFormData({ ...formData, portalAccess: [e.target.value] })}
                            input={<OutlinedInput label="Portal Access" />}
                            disabled={createMutation.isPending || updateMutation.isPending}
                          >
                            {portals.map((portal) => (
                              <MenuItem key={portal} value={portal}>
                                <ListItemText primary={portal.charAt(0).toUpperCase() + portal.slice(1)} />
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </FormControl>
                  </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                  <Typography variant="h6" gutterBottom>
                    Status
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        disabled={createMutation.isPending || updateMutation.isPending}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>  
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isAdminRole()
                  ? 'Admin roles can access multiple portals. Select which portals this role can access.'
                  : 'Non-admin roles can only access one portal. Select the primary portal for this role.'
                }
              </Typography>  
            </Paper>
          </Grid>

         

          {/* Action Buttons */}
          <Grid size={{xs: 12, sm: 6}}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/roles')}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={handleSubmit}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  isEdit ? 'Update Role' : 'Create Role'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default CreateEditRole;
